import { formatCents } from '@auctisync/shared';

import type { AuctisyncSupabase } from './client.js';
import type { PriceKey } from './lot-drafts.js';
import { MSRP_LABEL } from './lot-facts.js';
import { DataRequestError } from './request-error.js';

/**
 * Which of a lot's prices a bidder sees — the house's choice, lot by lot.
 *
 * Jim, 2026-10-06, reviewing his test sale: "I should be able to select a
 * checkbox next to each 'Price to review' so I can decide which prices are
 * visible on the listing. MSRP should be pre-checked. These pre-check options
 * should be available in settings." Until then MSRP was the one price a bidder
 * saw, always (2026-09-23).
 *
 * Three prices can be shown: MSRP, retail price and the estimate (low–high, one
 * line). The starting bid is always shown — bidding opens there — and the
 * reserve never is.
 *
 * 🔴 THE DATA DECIDES, NOT THE SCREEN. A bidder's browser and the app read
 * `listing_*` columns that the server fills only when the box is ticked
 * (migration 20261006130000). An unticked retail price or estimate never
 * leaves the server. Every surface shows prices through `listingPriceLines`,
 * so the website and the app cannot disagree about which prices show or how
 * they read.
 */

export type ListingPriceKey = 'msrp' | 'retail_price' | 'estimate';

export type ListingPriceSettings = Record<ListingPriceKey, boolean>;

/** A new house: MSRP shown, the rest not (Jim: "MSRP should be pre-checked"). */
export const DEFAULT_LISTING_PRICE_SETTINGS: Readonly<ListingPriceSettings> = {
  msrp: true,
  retail_price: false,
  estimate: false,
};

export const RETAIL_PRICE_LABEL = 'Retail price';
export const ESTIMATE_LABEL = 'Estimate';

/** The checkbox's words, on every staff surface. */
export const SHOW_ON_LISTING_LABEL = 'Show on listing';

/**
 * Where a house changes which prices a new lot shows: the Pricing box of AI
 * Settings on the office website, beside which prices AI research fills in.
 *
 * Jim, 2026-10-06, ticked every price in that box and found his lots still
 * showed bidders only the MSRP — the defaults then lived on a page of their
 * own. One box now holds both choices; the page's section carries this id.
 */
export const LISTING_PRICE_SETTINGS_SECTION = 'pricing';
export const LISTING_PRICE_SETTINGS_PATH = `/admin/ai-settings#${LISTING_PRICE_SETTINGS_SECTION}`;

export interface ListingPriceOption {
  key: ListingPriceKey;
  label: string;
  /** The lot's checkbox column. */
  column: 'show_msrp' | 'show_retail_price' | 'show_estimate';
}

/** The three choices, in the order every surface lists them. */
export const LISTING_PRICE_OPTIONS: readonly ListingPriceOption[] = [
  { key: 'msrp', label: MSRP_LABEL, column: 'show_msrp' },
  { key: 'retail_price', label: RETAIL_PRICE_LABEL, column: 'show_retail_price' },
  { key: 'estimate', label: ESTIMATE_LABEL, column: 'show_estimate' },
];

/**
 * What "Show on listing" says beside each price a person reviews: the choice
 * it belongs to (both ends of the estimate share one), or that the price
 * always shows (the starting bid — bidding opens there) or never does (the
 * reserve).
 */
export type PriceListing = ListingPriceKey | 'always' | 'never';

export function listingForPrice(price: PriceKey): PriceListing {
  switch (price) {
    case 'msrp_cents':
      return 'msrp';
    case 'retail_price_cents':
      return 'retail_price';
    case 'estimate_min_cents':
    case 'estimate_max_cents':
      return 'estimate';
    case 'starting_bid_cents':
      return 'always';
    case 'reserve_cents':
      return 'never';
  }
}

/** The words for a price that has no box. */
export const PRICE_LISTING_TEXT: Record<'always' | 'never', string> = {
  always: 'Always',
  never: 'Never',
};

/**
 * A house's stored defaults, read defensively: anything missing or not a
 * boolean falls back to the default, so a malformed row can never show a
 * price the house did not choose to show… except MSRP, whose default is on.
 */
export function parseListingPriceSettings(raw: unknown): ListingPriceSettings {
  const r = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  const pick = (k: ListingPriceKey): boolean => {
    const v = r[k];
    return typeof v === 'boolean' ? v : DEFAULT_LISTING_PRICE_SETTINGS[k];
  };
  return { msrp: pick('msrp'), retail_price: pick('retail_price'), estimate: pick('estimate') };
}

/** A lot's three checkboxes, as settings. */
export function lotListingChoices(lot: {
  show_msrp: boolean | null;
  show_retail_price: boolean | null;
  show_estimate: boolean | null;
}): ListingPriceSettings {
  return {
    msrp: lot.show_msrp ?? DEFAULT_LISTING_PRICE_SETTINGS.msrp,
    retail_price: lot.show_retail_price ?? DEFAULT_LISTING_PRICE_SETTINGS.retail_price,
    estimate: lot.show_estimate ?? DEFAULT_LISTING_PRICE_SETTINGS.estimate,
  };
}

/** The house's defaults for a new lot. Staff of the house can read them. */
export async function fetchListingPriceSettings(
  supabase: AuctisyncSupabase,
  tenantId: string,
): Promise<ListingPriceSettings> {
  const { data, error } = await supabase
    .from('tenants')
    .select('listing_price_settings')
    .eq('id', tenantId)
    .maybeSingle();
  if (error) throw new DataRequestError(error);
  return parseListingPriceSettings(data?.listing_price_settings);
}

/** Change the house's defaults. The owner only — the server refuses anyone else (42501). */
export async function saveListingPriceSettings(
  supabase: AuctisyncSupabase,
  settings: ListingPriceSettings,
  tenantId?: string,
): Promise<ListingPriceSettings> {
  const { data, error } = await supabase.rpc('set_listing_price_settings', {
    p_settings: settings,
    ...(tenantId ? { p_tenant_id: tenantId } : {}),
  });
  if (error) throw new DataRequestError(error);
  return parseListingPriceSettings(data);
}

/**
 * How many of the house's lots that haven't closed show different prices from
 * its SAVED defaults — the number the office asks with ("Also change the 10
 * lots that haven't closed?"). Counts only; changes nothing. Owner only.
 */
export async function countLotsOffListingDefaults(
  supabase: AuctisyncSupabase,
  tenantId: string,
): Promise<number> {
  const { data, error } = await supabase.rpc('apply_listing_price_defaults', {
    p_tenant_id: tenantId,
    p_dry_run: true,
  });
  if (error) throw new DataRequestError(error);
  return data;
}

/**
 * Give the house's lots that haven't closed (draft, scheduled, live, closing)
 * its saved defaults; returns how many lots changed. Sold, unsold, no-sale and
 * cancelled lots keep theirs. Owner only — the server refuses anyone else.
 */
export async function applyListingDefaultsToOpenLots(
  supabase: AuctisyncSupabase,
  tenantId: string,
): Promise<number> {
  const { data, error } = await supabase.rpc('apply_listing_price_defaults', {
    p_tenant_id: tenantId,
  });
  if (error) throw new DataRequestError(error);
  return data;
}

/**
 * Tick or untick prices on one lot. Staff of the lot's house only (the lots
 * update policy). Nothing is read back: the boxes are staff-only columns, so a
 * caller re-reads the lot through `lots_staff`.
 */
export async function setLotListingPrices(
  supabase: AuctisyncSupabase,
  lotId: string,
  patch: Partial<ListingPriceSettings>,
): Promise<void> {
  const update: { show_msrp?: boolean; show_retail_price?: boolean; show_estimate?: boolean } = {};
  for (const o of LISTING_PRICE_OPTIONS) {
    const v = patch[o.key];
    if (typeof v === 'boolean') update[o.column] = v;
  }
  if (Object.keys(update).length === 0) return;
  const { error } = await supabase.from('lots').update(update).eq('id', lotId);
  if (error) throw new DataRequestError(error);
}

// ─── What a bidder sees ──────────────────────────────────────────────────────

/** The bidder-readable prices on a public lot row (`PUBLIC_LOT_COLUMNS`). */
export interface ListingPriceInput {
  listing_msrp_cents?: number | null;
  listing_retail_price_cents?: number | null;
  listing_estimate_min_cents?: number | null;
  listing_estimate_max_cents?: number | null;
}

/**
 * The bidder copies for prices and choices that are not saved yet — what the
 * server's `trg_lots_listing_prices` will store: a price when its box is
 * ticked, nothing when it is not. For the office's "Preview as bidder", which
 * shows the form before Save.
 */
export function listingCopies(
  prices: {
    msrp_cents: number | null;
    retail_price_cents: number | null;
    estimate_min_cents: number | null;
    estimate_max_cents: number | null;
  },
  choices: ListingPriceSettings,
): ListingPriceInput {
  return {
    listing_msrp_cents: choices.msrp ? prices.msrp_cents : null,
    listing_retail_price_cents: choices.retail_price ? prices.retail_price_cents : null,
    listing_estimate_min_cents: choices.estimate ? prices.estimate_min_cents : null,
    listing_estimate_max_cents: choices.estimate ? prices.estimate_max_cents : null,
  };
}

export interface ListingPriceLine {
  key: ListingPriceKey;
  label: string;
  value: string;
}

const shown = (cents: number | null | undefined): number | null =>
  cents == null || !Number.isFinite(cents) || cents <= 0 ? null : cents;

/**
 * The price lines a bidder sees on a lot, in order: MSRP, retail price,
 * estimate. A price the house did not tick arrives as null and makes no line;
 * so does a zero — "$0.00" would read as a claim the item is worth nothing.
 *
 * The estimate is one line: "$45.00 – $75.00", or one figure when the house
 * set only one end ("From $45.00", "Up to $75.00") or both ends alike.
 */
export function listingPriceLines(lot: ListingPriceInput): ListingPriceLine[] {
  const lines: ListingPriceLine[] = [];
  const msrp = shown(lot.listing_msrp_cents);
  if (msrp !== null) lines.push({ key: 'msrp', label: MSRP_LABEL, value: formatCents(msrp) });
  const retail = shown(lot.listing_retail_price_cents);
  if (retail !== null) {
    lines.push({ key: 'retail_price', label: RETAIL_PRICE_LABEL, value: formatCents(retail) });
  }
  const estimate = estimateText(
    shown(lot.listing_estimate_min_cents),
    shown(lot.listing_estimate_max_cents),
  );
  if (estimate !== null) lines.push({ key: 'estimate', label: ESTIMATE_LABEL, value: estimate });
  return lines;
}

function estimateText(min: number | null, max: number | null): string | null {
  if (min !== null && max !== null) {
    if (min === max) return formatCents(min);
    const [lo, hi] = min < max ? [min, max] : [max, min];
    return `${formatCents(lo)} – ${formatCents(hi)}`;
  }
  if (min !== null) return `From ${formatCents(min)}`;
  if (max !== null) return `Up to ${formatCents(max)}`;
  return null;
}
